import type React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {generatePath, useNavigate} from "react-router";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Skeleton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import {routes} from "../../routing/routes";
import {useStores} from "../../stores";
import {notificationStore} from "../../stores/init";
import {useDeleteDashboardMutation} from "../chat-agent/hooks/useDeleteDashboardMutation";
import {useRenameDashboardMutation} from "../chat-agent/hooks/useRenameDashboardMutation";
import type {EmbeddedDashboardView} from "../chat-agent/types/chatTypes";
import type {DashboardItem} from "./services/dashboardsApi";
import {useHermesDashboards, useInvalidateMyDashboards, useMyDashboards} from "./services/dashboardsQueries";
import {DashboardCard} from "./DashboardCard";
import {AtlasDashboardPanel} from "./AtlasDashboardPanel";
import {filterGallery, GALLERY_FILTERS, type GalleryFilter} from "./dashboards.utils";
import "./dashboards.scss";

/** How long a deleted dashboard can be restored before the DELETE is sent. */
export const DELETE_UNDO_MS = 8000;

type PendingDelete = {dashboard: DashboardItem; timer: ReturnType<typeof setTimeout>; toastId: string};

const GallerySkeleton: React.FC = () => (
    <section className="dsh-sec">
        <Skeleton variant="text" width={180} height={24}/>
        <div className="dsh-ggrid">
            {Array.from({length: 4}, (_, i) => <Skeleton key={i} variant="rounded" height={200} sx={{borderRadius: "12px"}}/>)}
        </div>
    </section>
);

export const DashboardsGallery: React.FC = observer(() => {
    const navigate = useNavigate();
    const {bagStore} = useStores();
    const bagId = bagStore.bagId;
    const hermes = useHermesDashboards();
    const mine = useMyDashboards(bagId);
    const invalidateMine = useInvalidateMyDashboards();
    const {mutateAsync: deleteDashboard} = useDeleteDashboardMutation();
    const {mutate: renameDashboard, isPending: isRenaming} = useRenameDashboardMutation();

    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState<GalleryFilter>("All");
    const [hiddenIds, setHiddenIds] = useState<ReadonlySet<string>>(new Set());
    const [renameTarget, setRenameTarget] = useState<DashboardItem | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [localTitles, setLocalTitles] = useState<Record<string, string>>({});
    const pending = useRef(new Map<string, PendingDelete>());

    const builtIn = useMemo(() => hermes.data?.dashboards ?? [], [hermes.data]);
    const myDashboards = useMemo(
        () => (mine.data?.dashboards ?? [])
            .filter(d => !hiddenIds.has(d.id))
            .map(d => localTitles[d.id] ? {...d, title: localTitles[d.id]} : d),
        [mine.data, hiddenIds, localTitles],
    );
    const sections = filterGallery(builtIn, myDashboards, query, filter);

    const isLoading = hermes.isPending || (Boolean(bagId) && mine.isPending);
    const unavailable = !isLoading && !hermes.isError && !mine.isError
        && !hermes.data?.available && !mine.data?.available;

    const openDashboard = (d: DashboardItem) => {
        navigate(generatePath(routes.dashboards_detail, {id: String(bagId), source: d.source, dashboardId: d.id}));
    };

    // "+ New dashboard" docks a fresh DashGen chat on the right; what it builds lands in Yours.
    const [atlasOpen, setAtlasOpen] = useState(false);
    const newDashboard = () => setAtlasOpen(true);
    const onDashboardChanged = useCallback(() => {
        void invalidateMine(bagId);
    }, [bagId, invalidateMine]);
    const openBuilt = (built: EmbeddedDashboardView) => {
        navigate(generatePath(routes.dashboards_detail, {
            id: String(bagId), source: "mine", dashboardId: String(built.dashboard_id),
        }));
    };

    // region delete with undo
    const unhide = (id: string) => setHiddenIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
    });

    const commitDelete = useCallback(async (p: PendingDelete) => {
        pending.current.delete(p.dashboard.id);
        try {
            await deleteDashboard({dashboardId: p.dashboard.dashgenId!});
        } catch {
            unhide(p.dashboard.id);
            notificationStore.show("Could not delete the dashboard. Please try again.", {
                severity: "error",
                id: `dsh-delete-error-${p.dashboard.id}`,
                autoHideDuration: 4000,
            });
            return;
        }
        await invalidateMine(bagId);
    }, [deleteDashboard, invalidateMine, bagId]);

    const requestDelete = (d: DashboardItem) => {
        if (d.dashgenId === null || pending.current.has(d.id)) return;
        setHiddenIds(prev => new Set(prev).add(d.id));
        const toastId = `dsh-delete-${d.id}`;
        const entry: PendingDelete = {
            dashboard: d,
            toastId,
            timer: setTimeout(() => void commitDelete(entry), DELETE_UNDO_MS),
        };
        pending.current.set(d.id, entry);
        notificationStore.show(`“${d.title}” deleted.`, {
            id: toastId,
            severity: "info",
            autoHideDuration: DELETE_UNDO_MS,
            actionText: "Undo",
            onAction: () => {
                const p = pending.current.get(d.id);
                if (!p) return;
                clearTimeout(p.timer);
                pending.current.delete(d.id);
                unhide(d.id);
                notificationStore.hide(toastId);
            },
        });
    };

    // Leaving the page commits pending deletes right away instead of dropping them.
    useEffect(() => {
        const map = pending.current;
        return () => {
            map.forEach(p => {
                clearTimeout(p.timer);
                notificationStore.hide(p.toastId);
                void commitDelete(p);
            });
        };
    }, [commitDelete]);
    // endregion

    // region rename
    const startRename = (d: DashboardItem) => {
        setRenameTarget(d);
        setRenameValue(d.title);
    };
    const submitRename = () => {
        const target = renameTarget;
        const next = renameValue.trim();
        if (!target || target.dashgenId === null) return;
        if (!next || next === target.title) {
            setRenameTarget(null);
            return;
        }
        renameDashboard({dashboardId: target.dashgenId, title: next}, {
            onSuccess: () => {
                setLocalTitles(t => ({...t, [target.id]: next}));
                setRenameTarget(null);
                void invalidateMine(bagId);
            },
            onError: () => {
                notificationStore.show("Could not rename the dashboard. Please try again.", {
                    severity: "error",
                    id: `dsh-rename-error-${target.id}`,
                    autoHideDuration: 4000,
                });
            },
        });
    };
    // endregion

    return (
        <div className={atlasOpen ? "dsh-shell dsh-shell--atlas" : "dsh-shell"}>
        <div className="dsh-shell-main">
        <div className="dsh-root dsh-page">
            <div className="dsh-head">
                <h1 className="dsh-t-page-title">Dashboards</h1>
                <Button
                    variant="contained"
                    className="dsh-btn-primary"
                    startIcon={<AddIcon/>}
                    onClick={newDashboard}
                    disabled={!bagId || atlasOpen}
                >
                    New dashboard
                </Button>
            </div>

            <div className="dsh-filters">
                <TextField
                    size="small"
                    placeholder="Search dashboards"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    className="dsh-search"
                    slotProps={{
                        htmlInput: {"aria-label": "Search dashboards"},
                        input: {
                            startAdornment: (
                                <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>
                            ),
                        },
                    }}
                />
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    className="dsh-seg"
                    value={filter}
                    onChange={(_, v: GalleryFilter | null) => v && setFilter(v)}
                    aria-label="Show"
                >
                    {GALLERY_FILTERS.map(f => <ToggleButton key={f} value={f}>{f}</ToggleButton>)}
                </ToggleButtonGroup>
            </div>

            {(hermes.isError || mine.isError) && (
                <Alert severity="error" className="dsh-alert">
                    Some dashboards could not be loaded. {(hermes.error ?? mine.error)?.message}
                </Alert>
            )}

            {isLoading ? <GallerySkeleton/> : unavailable ? (
                <div className="dsh-empty">
                    <DashboardOutlinedIcon sx={{fontSize: 28}}/>
                    <p className="dsh-t-card-title">Dashboards are not enabled for this environment</p>
                </div>
            ) : sections.noMatch ? (
                <div className="dsh-empty">
                    <DashboardOutlinedIcon sx={{fontSize: 28}}/>
                    <p className="dsh-t-card-title">No dashboards match “{query.trim()}”</p>
                    <Button size="small" variant="outlined" onClick={() => {
                        setQuery("");
                        setFilter("All");
                    }}>Clear search</Button>
                </div>
            ) : <>
                {sections.showBuiltIn && (
                    <section className="dsh-sec" aria-labelledby="dsh-sec-builtin">
                        <div className="dsh-sec-h">
                            <h2 id="dsh-sec-builtin" className="dsh-t-section-title">Built-in dashboards</h2>
                            <p className="dsh-t-subtitle">Maintained by Mithra. View only.</p>
                        </div>
                        {sections.builtIn.length ? (
                            <div className="dsh-ggrid">
                                {sections.builtIn.map(d => <DashboardCard key={d.id} dashboard={d} onOpen={openDashboard}/>)}
                            </div>
                        ) : (
                            <div className="dsh-empty dsh-empty--inline">
                                <p className="dsh-t-body-sm">
                                    {query.trim() ? "No built-in dashboards match." : "No built-in dashboards have been published yet."}
                                </p>
                            </div>
                        )}
                    </section>
                )}
                {sections.showMine && (
                    <section className="dsh-sec" aria-labelledby="dsh-sec-mine">
                        <div className="dsh-sec-h">
                            <h2 id="dsh-sec-mine" className="dsh-t-section-title">Your dashboards</h2>
                            <p className="dsh-t-subtitle">Built by you. Open one and edit it with Atlas.</p>
                        </div>
                        {sections.mine.length ? (
                            <div className="dsh-ggrid">
                                {sections.mine.map(d => (
                                    <DashboardCard
                                        key={d.id}
                                        dashboard={d}
                                        onOpen={openDashboard}
                                        onRename={startRename}
                                        onDelete={requestDelete}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="dsh-empty dsh-empty--inline">
                                <p className="dsh-t-body-sm">
                                    {query.trim() ? "None of your dashboards match." : "You haven't built a dashboard yet."}
                                </p>
                                {!query.trim() && bagId > 0 && (
                                    <Button size="small" variant="outlined" onClick={newDashboard}>New dashboard</Button>
                                )}
                            </div>
                        )}
                    </section>
                )}
            </>}

            <Dialog className="dsh-portal" open={renameTarget !== null} onClose={() => !isRenaming && setRenameTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Rename dashboard</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        margin="dense"
                        value={renameValue}
                        disabled={isRenaming}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") submitRename();
                        }}
                        slotProps={{htmlInput: {"aria-label": "Dashboard name", maxLength: 250}}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameTarget(null)} disabled={isRenaming}>Cancel</Button>
                    <Button variant="contained" className="dsh-btn-primary" onClick={submitRename} disabled={isRenaming || !renameValue.trim()}>
                        Rename
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
        </div>
            {atlasOpen && (
                <AtlasDashboardPanel
                    mode={{kind: "new"}}
                    onClose={() => setAtlasOpen(false)}
                    onDashboardChanged={onDashboardChanged}
                    onOpenDashboard={openBuilt}
                />
            )}
        </div>
    );
});
